import { ChangeEvent, SyntheticEvent, useEffect, useState } from "react";
import { saveUserInfoValues } from "@/utils/userInfoData";
import { validateDob } from "@/utils/validateField/dob";
import {
  EMAIL_OR_PHONE_TAKEN_ERROR_MESSAGE,
  EMAIL_REGEX,
  NAME_FIELD_REGEX,
  PHONE_REGEX,
} from "@/constants";
import { InputField } from "@/components/common/input";
import { NoteIcon } from "@/components/common/icons/NoteIcon";
import { QuizQuestionProps } from "@/pages/quiz/context/types";
import { Button } from "@/components/common/button";

const NUMBER_OF_ANSWERS = 5;

const DEFAULT_ERROR_MESSAGE = "Required";
const FIRST_NAME_ERROR_MESSAGE = "Enter valid first name";
const SECOND_NAME_ERROR_MESSAGE = "Enter valid last name";
const EMAIL_ERROR_MESSAGE = "Enter valid email";
const PHONE_ERROR_MESSAGE =
  "Invalid phone number format. It should be in the format (222) 345-6789";

type FormFieldName = "firstName" | "lastName" | "dob" | "phone" | "email";

type ContactFieldsValues = Record<FormFieldName, string>;

const ContactFieldsCheckout = ({
  question,
  questionValue,
  setQuestionValue,
  setQuestionAnswer,
}: QuizQuestionProps<ContactFieldsValues>) => {
  const [error, setError] = useState<Record<string, string | boolean>>({});
  // some fields are additionally validated on server

  useEffect(() => {
    saveUserInfoValues(questionValue);
  }, [questionValue]);

  const isAllAnswered =
    Object.values(questionValue).filter(Boolean).length === NUMBER_OF_ANSWERS;

  const isNoErrors = Object.values(error).every(
    (errorMessage) => !errorMessage
  );

  const isFormAnswered = isAllAnswered && isNoErrors;

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name;
    const value = e.target.value;

    setQuestionValue({ ...questionValue, [name]: value });

    if (error[name]) {
      validateField(name, value);
    }
  };

  const setFieldError = (field: string, errorMessage: string | boolean) => {
    setError((errors) => ({ ...errors, [field]: errorMessage }));
  };

  const validateField = (name: string, value: string) => {
    if (!value || value.length < 2) {
      setFieldError(name, DEFAULT_ERROR_MESSAGE);
      return;
    }

    if (name === "dob") {
      const dobErrorMessage = validateDob(value || "");

      setFieldError(name, dobErrorMessage || false);
      return;
    }

    if (name === "phone") {
      const isValid = PHONE_REGEX.test(value || "");
      setFieldError(name, !isValid && PHONE_ERROR_MESSAGE);
      return;
    }

    if (name === "email") {
      const isValid = EMAIL_REGEX.test(value || "");
      setFieldError(name, !isValid && EMAIL_ERROR_MESSAGE);
      return;
    }

    if (name === "lastName") {
      const isValid = NAME_FIELD_REGEX.test(value);
      setFieldError(name, !isValid && SECOND_NAME_ERROR_MESSAGE);
      return;
    }

    if (name === "firstName") {
      const isValid = NAME_FIELD_REGEX.test(value);
      setFieldError(name, !isValid && FIRST_NAME_ERROR_MESSAGE);
      return;
    }

    setFieldError(name, DEFAULT_ERROR_MESSAGE);

    // valid field
    // setFieldError(name, "");
  };

  const handleBlur = (e: SyntheticEvent<HTMLInputElement>) => {
    const name = (e.target as HTMLInputElement).name;

    validateField(name, questionValue[name as keyof ContactFieldsValues]);
  };

  const registerField = (name: FormFieldName) => ({
    value: questionValue[name],
    name,
    onChange: handleInputChange,
    error: error[name],
    onBlur: handleBlur,
  });

  const onSubmit = async () => {
    if (isFormAnswered) {
      const serverErrors = await setQuestionAnswer(
        {
          ...questionValue,
          email: questionValue.email.toLowerCase(),
        },
        true
      );

      if (serverErrors?.serverError?.status === 409) {
        setFieldError("email", EMAIL_OR_PHONE_TAKEN_ERROR_MESSAGE);
      }
    }
  };

  return (
    <div className=" flex flex-col-reverse items-center	 w-full justify-center gap-4 desktop:p-8">
      <div className="text-center max-w-[100%] w-[480px] flex-col flex gap-2">
        <h4
          color="red"
          className="text-center uppercase section-title xs:text-2xl text-xl"
        >
          Final Steps
        </h4>
        <div>
          <p className="section-title !text-[17px]">
            <NoteIcon />
            If prescribed, our providers require your personal information on
            file.
          </p>
        </div>

        <div className="flex direction-row gap-5 ">
          <InputField {...registerField("firstName")} label="First Name" />
          {/* <InputField {...registerField("lastName")} label="Last Name" /> */}
        </div>

        <InputField
          {...registerField("dob")}
          label="Date of birth"
          type="dob"
          onChange={(event) => {
            const caret = event.target.selectionStart;
            const element = event.target;
            window.requestAnimationFrame(() => {
              element.selectionStart = caret;
              element.selectionEnd = caret;
            });
            handleInputChange(event);
          }}
        />

        <InputField {...registerField("phone")} label="Phone" type="phone" />

        <InputField {...registerField("email")} label="Email" />

        <Button disabled={!isFormAnswered} onClick={onSubmit} />
      </div>
    </div>
  );
};

export { ContactFieldsCheckout };
